import React, { useEffect, useMemo, useState } from 'react';
import { DefaultPageLayout } from '../components/layouts/DefaultPageLayout';
import BunnyLoader from '../components/ui/BunnyLoader';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { TextField } from '../components/ui/TextField';
import { IconWithBackground } from '../components/ui/icon-with-background';
import { FeatherCalendar, FeatherRefreshCw } from '../components/ui/icons';
import { financeApi } from '../services/api';
import './FinanceAttendance.css';

const LEAVE_QUOTA_STATUS = {
  within_quota: { label: 'Within quota', variant: 'success' },
  low: { label: 'Low', variant: 'warning' },
  exhausted: { label: 'Exhausted', variant: 'error' }
};

function LeaveYearCell({ row }) {
  const quota = row.annualLeaveQuota ?? 18;
  const used = row.annualLeavesUsed ?? 0;
  const remaining = row.annualLeavesRemaining ?? Math.max(0, quota - used);
  const status = LEAVE_QUOTA_STATUS[row.leaveQuotaStatus] || LEAVE_QUOTA_STATUS.within_quota;
  const usedPct = quota > 0 ? Math.min(100, (used / quota) * 100) : 0;

  return (
    <div className="leave-year-cell">
      <div className="leave-year-used">
        {used} of {quota} used
      </div>
      <div className="muted leave-year-remaining">
        {remaining > 0 ? `${remaining} remaining` : 'None remaining'}
      </div>
      <div className="leave-year-progress" aria-hidden="true">
        <div className={`leave-year-progress-bar ${row.leaveQuotaStatus || ''}`} style={{ width: `${usedPct}%` }} />
      </div>
      <Badge variant={status.variant}>{status.label}</Badge>
    </div>
  );
}

const FinanceAttendance = () => {
  const now = useMemo(() => new Date(), []);
  const [month, setMonth] = useState(() => {
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`; // YYYY-MM
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);

  const { year, monthNumber } = useMemo(() => {
    const [y, m] = month.split('-');
    return { year: Number(y), monthNumber: Number(m) };
  }, [month]);

  const fetchData = async () => {
    setError('');
    setLoading(true);
    try {
      const data = await financeApi.getMonthlyAttendance(year, monthNumber);
      setRows(Array.isArray(data?.members) ? data.members : Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || 'Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, monthNumber]);

  return (
    <DefaultPageLayout>
      <div className="finance-attendance-container">
        <div className="header">
          <div className="header-left">
            <IconWithBackground icon={<FeatherCalendar />} />
            <div>
              <h1 className="text-heading-2">Attendance</h1>
              <p className="text-body text-subtext-color">
                Monthly attendance plus yearly leave quota ({year}). Days beyond 18 approved weekday leaves are LOP.
              </p>
            </div>
          </div>
          <div className="header-right">
            <Button variant="neutral-tertiary" icon={<FeatherRefreshCw />} onClick={fetchData} disabled={loading}>
              Refresh
            </Button>
          </div>
        </div>

        <div className="filters-card">
          <TextField label="Month">
            <TextField.Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
          </TextField>
        </div>

        {loading && (
          <div className="loading-container">
            <BunnyLoader size="large" text="Loading attendance..." animation="bounce" />
          </div>
        )}

        {!loading && error && (
          <div className="inline-alert">
            {error}
          </div>
        )}

        {!loading && !error && (
          <div className="table-card">
            <div className="overflow-auto">
              <table>
                <thead>
                <tr>
                  <th className="text-left p-3">Resource</th>
                  <th className="text-right p-3">Days in month</th>
                  <th className="text-right p-3">Work plan days</th>
                  <th className="text-right p-3">Approved this month</th>
                  <th className="text-left p-3">Leave this year</th>
                  <th className="text-right p-3">Holidays</th>
                  <th className="text-right p-3">Weekends</th>
                  <th className="text-right p-3">Days present</th>
                  <th className="text-right p-3">LOP days</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td className="muted" colSpan={9}>
                      No data for selected month.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.resourceId || r.id} className="border-t border-neutral-100">
                      <td className="p-3">
                        <div className="font-medium">{r.name || r.display_name || 'Unknown'}</div>
                        {r.email && <div className="text-xs muted">{r.email}</div>}
                      </td>
                      <td className="p-3 text-right">{r.daysInMonth ?? '-'}</td>
                      <td className="p-3 text-right">{r.workPlanDays ?? '-'}</td>
                      <td className="p-3 text-right">{r.approvedLeaveDays ?? '-'}</td>
                      <td className="p-3">
                        <LeaveYearCell row={r} />
                      </td>
                      <td className="p-3 text-right">{r.holidayDays ?? '-'}</td>
                      <td className="p-3 text-right">{r.weekendDays ?? '-'}</td>
                      <td className="p-3 text-right font-semibold">{r.daysPresent ?? '-'}</td>
                      <td className="p-3 text-right">
                        {r.lopDays ?? r.lopCount ?? '-'}
                        {r.excessLeaveLopDays > 0 && (
                          <div className="muted lop-excess-note">
                            incl. {r.excessLeaveLopDays} over-quota
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </DefaultPageLayout>
  );
};

export default FinanceAttendance;

